// src/components/PostExperiment.js
import React, { useEffect, useState, useRef } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Table } from 'react-bootstrap';
import { LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import useDebugTools, { debugSetStep } from '../../hooks/useDebugTools';
import useProgressRedirect from '../../hooks/useProgressRedirect';
import ProgressTracker from '../shared/ProgressTracker';

// Groups consecutive questions of the same type+choices into visual blocks
function groupQuestions(questions) {
  const groups = [];
  let i = 0;
  let num = 0;

  while (i < questions.length) {
    const q = questions[i];

    // Likert: consecutive MCQs sharing identical choices → table
    if (q.question_type === 'MCQ' && Array.isArray(q.choices) && q.choices.length > 0) {
      const key = q.choices.join('|||');
      const block = [{ q, num: num + 1 }];
      let j = i + 1;
      while (
        j < questions.length &&
        questions[j].question_type === 'MCQ' &&
        Array.isArray(questions[j].choices) &&
        questions[j].choices.join('|||') === key
      ) {
        block.push({ q: questions[j], num: num + (j - i) + 1 });
        j++;
      }
      if (block.length >= 2) {
        groups.push({ type: 'likert', items: block, choices: q.choices });
        num += block.length;
        i = j;
        continue;
      }
    }

    // NUMERIC cluster: consecutive NUMERIC questions → compact list card
    if (q.question_type === 'NUMERIC') {
      const block = [{ q, num: num + 1 }];
      let j = i + 1;
      while (j < questions.length && questions[j].question_type === 'NUMERIC') {
        block.push({ q: questions[j], num: num + (j - i) + 1 });
        j++;
      }
      if (block.length >= 2) {
        groups.push({ type: 'numeric-group', items: block });
        num += block.length;
        i = j;
        continue;
      }
    }

    groups.push({ type: 'single', q, num: num + 1 });
    num++;
    i++;
  }
  return groups;
}

function DraggableRanking({ qId, order, onReorder }) {
  const dragFrom = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  const handleDragStart = (idx) => { dragFrom.current = idx; };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (hoverIdx !== idx) setHoverIdx(idx);
  };
  const handleDrop = (toIdx) => {
    if (dragFrom.current !== null && dragFrom.current !== toIdx) {
      const next = [...order];
      const [moved] = next.splice(dragFrom.current, 1);
      next.splice(toIdx, 0, moved);
      onReorder(next);
    }
    dragFrom.current = null;
    setHoverIdx(null);
  };
  const handleDragEnd = () => { dragFrom.current = null; setHoverIdx(null); };

  return (
    <div>
      <p className="text-muted small mb-2">Drag items to reorder — #1 = most useful.</p>
      {order.map((label, idx) => (
        <div
          key={label}
          draggable
          onDragStart={() => handleDragStart(idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDrop={() => handleDrop(idx)}
          onDragEnd={handleDragEnd}
          className="d-flex align-items-center mb-2 px-3 py-2 rounded border"
          style={{
            cursor: 'grab',
            background: hoverIdx === idx && dragFrom.current !== idx ? '#dbeafe' : '#fff',
            borderColor: hoverIdx === idx && dragFrom.current !== idx ? '#0d6efd' : '#dee2e6',
            userSelect: 'none',
            transition: 'background 0.1s, border-color 0.1s',
          }}
        >
          <span className="text-muted me-3" style={{ fontSize: '1.1rem' }}>⠿</span>
          <span
            className="badge rounded-pill me-3"
            style={{ background: '#0d6efd', minWidth: '26px', textAlign: 'center' }}
          >
            {idx + 1}
          </span>
          <span style={{ fontSize: '0.95rem' }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

function PostExperiment() {
  const { token, getValidToken } = useToken();
  const debugTools = useDebugTools();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [rankOrders, setRankOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useProgressRedirect('POST_EXPERIMENT');

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await fetch('/api/questionnaire/post', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setQuestions(data);

        const orders = {};
        const initAnswers = {};
        data.forEach(q => {
          if (q.question_type === 'RANKING' && Array.isArray(q.choices)) {
            const order = q.choices.map(c => (typeof c === 'string' ? c : c.text));
            orders[q.id] = order;
            // Default ranking = original order; user must drag to change
            const rankMap = {};
            order.forEach((label, idx) => { rankMap[label] = String(idx + 1); });
            initAnswers[q.id] = rankMap;
          }
        });
        setRankOrders(orders);
        setAnswers(initAnswers);
        setLoading(false);
      } catch {
        setError('Failed to load questionnaire.');
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [token]);

  const handleInputChange = (qId, value) =>
    setAnswers(prev => ({ ...prev, [qId]: value }));

  const handleCheckboxChange = (qId, choice) => {
    setAnswers(prev => {
      const cur = prev[qId] || [];
      return {
        ...prev,
        [qId]: cur.includes(choice) ? cur.filter(x => x !== choice) : [...cur, choice],
      };
    });
  };

  const handleRankReorder = (qId, newOrder) => {
    setRankOrders(prev => ({ ...prev, [qId]: newOrder }));
    const rankMap = {};
    newOrder.forEach((label, idx) => { rankMap[label] = String(idx + 1); });
    setAnswers(prev => ({ ...prev, [qId]: rankMap }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const unanswered = questions.filter(q => {
      const a = answers[q.id];
      if (q.question_type === 'TEXT' || q.question_type === 'NUMERIC')
        return !a || a.trim() === '';
      if (q.question_type === 'MCQ') return !a;
      if (q.question_type === 'CHECKBOX') return !a || a.length === 0;
      if (q.question_type === 'RANKING') {
        if (!a || typeof a !== 'object') return true;
        return q.choices.some(c => {
          const label = typeof c === 'string' ? c : c.text;
          return !a[label];
        });
      }
      return false;
    });

    if (unanswered.length > 0) {
      setError('Please answer all questions before submitting.');
      setSubmitting(false);
      return;
    }

    if (!window.confirm('Are you sure you want to submit? You cannot change your answers after submission.')) {
      setSubmitting(false);
      return;
    }

    const formatted = Object.entries(answers).map(([qId, a]) => ({
      questionnaire_id: parseInt(qId),
      answer: Array.isArray(a)
        ? a.join(', ')
        : typeof a === 'object'
          ? JSON.stringify(a)
          : a,
    }));

    try {
      const authToken = await getValidToken();
      const res = await fetch('/api/questionnaire/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ answers: formatted, type: 'POST' }),
      });

      if (res.ok) {
        const progressRes = await fetch('/api/player/progress', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ progress_step: 'FOLLOW_UP' }),
        });
        if (progressRes.ok) { navigate('/follow-up', { replace: true }); return; }
        const err = await progressRes.json();
        setError(err.message || 'Failed to update progress.');
      } else {
        const err = await res.json();
        setError(err.message || 'Failed to submit answers.');
      }
    } catch {
      setError('Error submitting answers.');
    }
    setSubmitting(false);
  };

  const groups = groupQuestions(questions);

  return (
    <Container className="py-4">
      <ProgressTracker currentStep="post_experiment" />
      {(loading || submitting) && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : (
        <Card className="shadow-lg border-0">
          <Card.Body className="p-4">
            <h2 className="mb-1">📝 Post-Experiment Questionnaire</h2>
            <p className="text-muted mb-4">
              Please answer all questions based on your experience during the experiment.
            </p>
            <Form onSubmit={handleSubmit}>

              {groups.map((group, gi) => {

                // ── Likert table ────────────────────────────────────────────
                if (group.type === 'likert') {
                  return (
                    <Card key={gi} className="mb-4 border" style={{ borderColor: '#dee2e6' }}>
                      <Card.Body className="p-0">
                        <div className="table-responsive">
                          <Table className="mb-0 align-middle" style={{ tableLayout: 'fixed' }}>
                            <thead style={{ background: '#f0f4ff' }}>
                              <tr>
                                <th style={{ width: '48%', padding: '10px 14px', fontWeight: 600 }}>
                                  Question
                                </th>
                                {group.choices.map((c, ci) => (
                                  <th
                                    key={ci}
                                    className="text-center"
                                    style={{ fontSize: '0.78rem', fontWeight: 600, padding: '10px 6px' }}
                                  >
                                    {c}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {group.items.map(({ q, num }, ri) => (
                                <tr
                                  key={q.id}
                                  style={{
                                    background: answers[q.id]
                                      ? 'rgba(13,110,253,0.04)'
                                      : ri % 2 === 0 ? '#fff' : '#fafafa',
                                  }}
                                >
                                  <td style={{ fontSize: '0.88rem', padding: '10px 14px' }}>
                                    <strong>{num}.</strong> {q.question_text}
                                  </td>
                                  {group.choices.map((c, ci) => (
                                    <td key={ci} className="text-center" style={{ padding: '10px 6px' }}>
                                      <Form.Check
                                        type="radio"
                                        name={`mcq-${q.id}`}
                                        value={c}
                                        checked={answers[q.id] === c}
                                        onChange={() => handleInputChange(q.id, c)}
                                        label=""
                                        className="d-flex justify-content-center m-0"
                                        style={{ transform: 'scale(1.2)' }}
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Card.Body>
                    </Card>
                  );
                }

                // ── NUMERIC cluster ─────────────────────────────────────────
                if (group.type === 'numeric-group') {
                  return (
                    <Card key={gi} className="mb-4 border" style={{ borderColor: '#dee2e6' }}>
                      <Card.Body className="p-3">
                        {group.items.map(({ q, num }, ri) => (
                          <div
                            key={q.id}
                            className={`d-flex align-items-center justify-content-between gap-3 py-2 ${ri < group.items.length - 1 ? 'border-bottom' : ''}`}
                          >
                            <Form.Label className="mb-0 flex-grow-1" style={{ fontSize: '0.9rem' }}>
                              <strong>{num}.</strong> {q.question_text}
                            </Form.Label>
                            <Form.Control
                              type="number"
                              step="any"
                              value={answers[q.id] || ''}
                              onChange={(e) => handleInputChange(q.id, e.target.value)}
                              placeholder="—"
                              style={{ width: '90px', flexShrink: 0 }}
                            />
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  );
                }

                // ── Single question ─────────────────────────────────────────
                const { q, num } = group;
                return (
                  <Card key={gi} className="mb-3 border" style={{ borderColor: '#dee2e6' }}>
                    <Card.Body className="p-3">
                      <Form.Label className="mb-3 d-block" style={{ fontSize: '0.95rem' }}>
                        <strong>{num}. {q.question_text}</strong>
                      </Form.Label>

                      {q.question_type === 'TEXT' && (
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={answers[q.id] || ''}
                          onChange={(e) => handleInputChange(q.id, e.target.value)}
                          placeholder="Your answer…"
                          style={{ resize: 'vertical' }}
                        />
                      )}

                      {q.question_type === 'NUMERIC' && (
                        <div style={{ maxWidth: '180px' }}>
                          <Form.Control
                            type="number"
                            step="any"
                            value={answers[q.id] || ''}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            placeholder="Enter a number"
                          />
                        </div>
                      )}

                      {q.question_type === 'RANKING' && rankOrders[q.id] && (
                        <DraggableRanking
                          qId={q.id}
                          order={rankOrders[q.id]}
                          onReorder={(newOrder) => handleRankReorder(q.id, newOrder)}
                        />
                      )}

                      {q.question_type === 'MCQ' && Array.isArray(q.choices) && (
                        <div className="d-flex flex-wrap gap-2">
                          {q.choices.map((choice, ci) => {
                            const label = typeof choice === 'string' ? choice : choice.text;
                            const selected = answers[q.id] === label;
                            return (
                              <Button
                                key={ci}
                                variant={selected ? 'primary' : 'outline-secondary'}
                                size="sm"
                                onClick={() => handleInputChange(q.id, label)}
                                style={{ fontWeight: selected ? 600 : 400 }}
                              >
                                {label}
                              </Button>
                            );
                          })}
                        </div>
                      )}

                      {q.question_type === 'CHECKBOX' && Array.isArray(q.choices) && (
                        <div>
                          {q.choices.map((choice, ci) => {
                            const label = typeof choice === 'string' ? choice : choice.text;
                            return (
                              <Form.Check
                                key={ci}
                                type="checkbox"
                                label={label}
                                checked={answers[q.id]?.includes(label) || false}
                                onChange={() => handleCheckboxChange(q.id, label)}
                                className="mb-1"
                              />
                            );
                          })}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })}

              {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
              <div className="d-flex gap-2 mt-4">
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? (
                    <><Spinner animation="border" size="sm" className="me-2" />Submitting…</>
                  ) : 'Submit'}
                </Button>
                {/* Testing shortcut: only rendered (and only accepted by the backend) with DEBUG_TOOLS=true */}
                {debugTools && (
                <Button
                  variant="warning"
                  onClick={async () => {
                    const authToken = await getValidToken();
                    const res = await debugSetStep(authToken, 'FOLLOW_UP');
                    if (res.ok) navigate('/follow-up', { replace: true });
                  }}
                >
                  ⚠️ Skip (Testing)
                </Button>
                )}
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default PostExperiment;
